/**
 * TanStack Query client configuration with localStorage persistence.
 *
 * - Query cache is persisted to localStorage so the job list survives page
 *   reloads without a Firestore round-trip.
 * - broadcastQueryClient is configured so that mutations in one tab immediately
 *   invalidate the cache in other open tabs (replaces the manual BroadcastChannel
 *   in useApplicationsCache).
 */
import { QueryClient } from "@tanstack/react-query";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { persistQueryClient } from "@tanstack/react-query-persist-client";

const CACHE_KEY = "hirecarta:query-cache";
// 24 hours — stale-while-revalidate keeps the list fresh in the background
const MAX_AGE_MS = 1000 * 60 * 60 * 24;

export function createQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        // Show cached data immediately on mount; refetch in background when stale
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: MAX_AGE_MS,
        retry: 1,
      },
    },
  });
  return client;
}

/**
 * Wire up localStorage persistence for the query client.
 * Must be called once on the client side (inside a useEffect or at module
 * top-level in a client component). Returns the cleanup function.
 */
export function setupQueryPersistence(
  client: QueryClient,
  userId: string,
): () => void {
  if (typeof window === "undefined") return () => {};

  const asyncLocalStorage = {
    getItem: (key: string) => Promise.resolve(window.localStorage.getItem(key)),
    setItem: (key: string, value: string) => {
      try {
        const parsed = JSON.parse(value);
        if (parsed?.clientState?.queries) {
          parsed.clientState.queries = parsed.clientState.queries.map((query: any) => {
            // Prune jobs list query
            if (query.queryKey?.[0] === "jobs" && Array.isArray(query.state?.data)) {
              return {
                ...query,
                state: {
                  ...query.state,
                  data: query.state.data.map((job: any) => {
                    const prunedJob = { ...job };
                    delete prunedJob.interviewPrep;
                    // Preserve lightweight fit evaluation summary for instant sidebar dots,
                    // while pruning the heavy detailed evaluation model to save localStorage space.
                    const fitSummary = job._fitEvaluation || (job.fitEvaluation ? {
                      overallScore: job.fitEvaluation.overallScore,
                      overallStatus: job.fitEvaluation.overallStatus,
                      fitStatus: job.fitStatus || { stage: "complete", message: "Analysis complete!" },
                    } : undefined);
                    delete prunedJob.fitEvaluation;
                    if (fitSummary) {
                      prunedJob._fitEvaluation = fitSummary;
                    } else {
                      delete prunedJob._fitEvaluation;
                    }
                    if (prunedJob.jobDescription) {
                      prunedJob.jobDescription = {
                        ...prunedJob.jobDescription,
                        description: "", // Keep company, title, URL but drop description
                      };
                    }
                    return prunedJob;
                  }),
                },
              };
            }
            return query;
          });
          const serialized = JSON.stringify(parsed);
          window.localStorage.setItem(key, serialized);
          return Promise.resolve();
        }
      } catch (e) {
        // Fallback to original value if parsing/pruning fails
        console.warn("[queryClient] Failed to prune cache payload, writing original", e);
      }
      window.localStorage.setItem(key, value);
      return Promise.resolve();
    },
    removeItem: (key: string) =>
      Promise.resolve(window.localStorage.removeItem(key)),
  };

  const persister = createAsyncStoragePersister({
    storage: asyncLocalStorage,
    key: `${CACHE_KEY}:${userId}`,
    throttleTime: 0, // Write to localStorage instantly to avoid race conditions during rapid navigation/reloads
  });

  const [unsubscribe] = persistQueryClient({
    queryClient: client,
    persister,
    maxAge: MAX_AGE_MS,
    dehydrateOptions: {
      // Replicate TanStack's default (only persist successful queries) and
      // also exclude userProfile — it's mutable user data that must always
      // be loaded fresh from Firestore, not from a stale persisted cache.
      shouldDehydrateQuery: (query) =>
        query.state.status === "success" &&
        query.queryKey[0] !== "userProfile" &&
        query.queryKey[0] !== "subscription" &&
        query.queryKey[0] !== "resumePreferences" &&
        query.queryKey[0] !== "coverLetterPreferences" &&
        query.queryKey[0] !== "goals" &&
        query.queryKey[0] !== "job",
    },
  });

  return unsubscribe;
}
